import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  AfterViewInit,
  ViewChildren,
  QueryList,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  StudioSceneCell,
  StudioService,
  StudioCell,
  LayoutMetaData,
  StudioSceneBackgroundDefinition,
  saveScene,
  StudioAnimationService,
} from '../../element-editor/store/studio';
import { StudioLayoutService } from '../../element-editor/store/studio/layout';
import { StudioScene } from '../../element-editor/store/studio/studio.types';
import { Application } from 'pixi.js';
import { StudioSceneShape } from '../../element-editor/shape/shapes/general/root/studio-scene-shape';
import { Subject } from 'rxjs';
import { CanvasService } from '../../services/canvas/canvas.service';
import { BackgroundService } from '../../element-editor/store/studio/background/background.service';

export interface LayoutOption {
  name: string;
  cells: Record<string, StudioSceneCell>;
}

@Component({
  selector: 'nw-previews-modal',
  templateUrl: './previews-modal.component.html',
  styleUrls: ['./previews-modal.component.scss'],
})
export class PreviewsModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private _previewMode: 'layout' | 'animation' | 'background';

  get title() {
    switch (this.previewMode) {
      case 'layout':
        return 'Layout';
      case 'animation':
        return 'Animation';
      case 'background':
        return 'Background';
    }
  }

  isLoading = false;

  start = 0;

  @Input()
  set previewMode(value: 'layout' | 'animation' | 'background') {
    if (this._previewMode === value) {
      return;
    }
    this.start = performance.now();

    this._previewMode = value;
    this.isLoading = true;
    if (!value) {
      this.cleanupPreviews();
      return;
    }
    this.initPreviews();
  }

  @Input()
  mode: 'animation' | 'layout' | 'background' = 'layout';

  // -- //
  get previewMode(): 'layout' | 'animation' | 'background' {
    return this._previewMode;
  }

  @Output() close = new EventEmitter<void>();

  @ViewChildren('previewCanvas') previewCanvases: QueryList<ElementRef>;

  layouts: {
    cells: Record<string, StudioCell>;
    layout: LayoutMetaData;
  }[] = [];
  selectedLayout: {
    cells: Record<string, StudioCell>;
    layout: LayoutMetaData;
  } | null = null;

  selectedPreviewScene: StudioScene | null = null;
  previewScenes: StudioScene[][] = [];
  currentPreviewScenes: StudioScene[][] = [];
  private previewApps: Application[] = [];
  private previewShapes: StudioSceneShape[] = [];
  private destroy$ = new Subject<void>();
  get currentScene() {
    return this.studioService.currentSceneShape?.scene;
  }

  constructor(
    private layoutService: StudioLayoutService,
    private backgroundService: BackgroundService,
    private store: Store,
    private studioService: StudioService,
    private animationService: StudioAnimationService,
    private cs: CanvasService,
  ) {}

  ngOnInit() {}
  ngAfterViewInit() {
    // Preview initialization is handled by the show setter
    // to avoid multiple re-initializations
  }
  bgs: StudioSceneBackgroundDefinition[][] = [];
  initPreviews() {
    console.log('initPreviews > previewMode', this.currentScene);
    switch (this.previewMode) {
      case 'layout':
        // const layout = this.layoutService.getPossibleLayouts();
        // console.log('initPreviews > layout', layout);
        // this.previewScenes = layout.map(layouts =>
        //   layouts.map(layout => this.getPreviewSceneDescriptor(layout)),
        // );
        // console.log('initPreviews > previewScenes', this.previewScenes);
        // this.currentPreviewScenes = this.previewScenes.slice(0, 2);

        this.previewScenes = this.layoutService.getPossibleScenes();
        console.log('initPreviews > previewScenes', this.previewScenes);
        break;
      case 'animation':
        this.previewScenes = this.animationService.getAnimationOptions(
          this.currentScene,
        );
        break;
      case 'background':
        this.bgs = this.backgroundService.getPreviews();
        this.previewScenes = this.bgs.map(bgs =>
          bgs.map(bg => ({
            ...this.currentScene,
            background: bg,
          })),
        );
        break;
    }
    this.currentPreviewScenes = this.previewScenes.slice(0, 2);

    setTimeout(() => {
      this.initPreviewApps();
    }, 10);
  }

  initPreviewApps() {
    // -- //
    this.cleanupPreviews();
    const canvasElements = this.previewCanvases.toArray();
    if (canvasElements.length === 0) {
      console.log('initPreviews: no canvas elements found');
      this.isLoading = false;
      return;
    }

    const scale = 240 / this.cs.currentScreenX;
    let index = 0;
    let innerIndex = 0;
    for (const scenes of this.currentPreviewScenes) {
      innerIndex = 0;
      for (const scene of scenes) {
        const canvasElement =
          canvasElements[index * scenes.length + innerIndex];
        if (!canvasElement) continue;

        // Create a PixiJS application for this preview
        const app = new Application({
          width: 240,
          height: 135, // 16:9 aspect ratio
          backgroundAlpha: 1,
          backgroundColor: 0xf9fafb,
          antialias: true,
          autoDensity: true,
          resolution: window.devicePixelRatio || 1,
        });

        // Append canvas to the container
        canvasElement.nativeElement.appendChild(app.view);
        this.previewApps.push(app);
        // console.log('scene', scene);
        const sceneShape = new StudioSceneShape(this.studioService, scene, {
          margin: 0,
          preview: true,
        });
        // -- //
        sceneShape.loadPromise.then(() => {
          console.log('loadPromise > resolved');
        });
        sceneShape.container.scale.set(scale);
        sceneShape.bgContainer.scale.set(scale);

        app.stage.addChild(sceneShape.bgContainer);
        app.stage.addChild(sceneShape.container);
        this.previewShapes.push(sceneShape);
        // Add to preview app
        innerIndex++;
      }
      index++;
    }
    console.log('initPreviewApps: done');
    this.isLoading = false;
    console.log('initPreviewApps: time', performance.now() - this.start);
  }

  getPreviewSceneDescriptor({
    cells,
    layout,
  }: {
    cells: Record<string, StudioCell>;
    layout: LayoutMetaData;
  }): StudioScene {
    if (!this.currentScene) {
      return null;
    }

    return {
      ...this.currentScene,
      shapes: Object.entries(this.currentScene.shapes).reduce(
        (object, [key, shape]) => ({
          ...object,
          [key]: {
            ...shape,
            cell: cells[key],
          },
        }),
        {},
      ),
      layout,
    };
  }

  cleanupPreviews() {
    console.log('cleanupPreviews');
    // Destroy all preview apps
    this.previewApps.forEach(app => {
      app.destroy(true, { children: true, texture: false, baseTexture: false });
    });
    this.previewApps = [];

    this.previewShapes.forEach(shape => {
      shape.remove();
    });
    // Clean up preview shapes
    // this.previewShapes.forEach(shape => {
    //   Object.values(shape.shapeCells).forEach(cell => cell.remove());
    // });
    this.previewShapes = [];
  }

  selectPreviewScene(scene: StudioScene) {
    // this.selectedPreviewScene = scene;
    this.store.dispatch(saveScene({ value: scene }));
    this.onClose();
  }

  selectLayout(layout: {
    cells: Record<string, StudioCell>;
    layout: LayoutMetaData;
  }) {
    this.selectedLayout = layout;
    this.applyLayout();
  }

  applyLayout() {
    if (this.selectedLayout) {
      // -- //
      this.onClose();
    }
  }

  onClose() {
    this.cleanupPreviews();
    this.close.emit();
  }

  ngOnDestroy() {
    this.cleanupPreviews();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Generate preview grid for a layout
  getCellStyle(cell: StudioSceneCell) {
    return {
      left: `${cell.x * 100}%`,
      top: `${cell.y * 100}%`,
      width: `${cell.w * 100}%`,
      height: `${cell.h * 100}%`,
    };
  }

  getCellType(cellKey: string): 'text' | 'image' | 'video' | 'unknown' {
    if (cellKey.startsWith('text:')) return 'text';
    if (cellKey.startsWith('image:')) return 'image';
    if (cellKey.startsWith('video:')) return 'video';
    return 'unknown';
  }
}
